class EditorialTheme {
  constructor() {
    this.body = document.body;
    this.root = window.Shopify?.routes?.root || '/';
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.shade = document.querySelector('[data-drawer-shade]');
    try { this.strings = JSON.parse(document.getElementById('ThemeStrings')?.textContent || '{}'); } catch { this.strings = {}; }
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.bindEvents();
    this.initialize(document);
    document.addEventListener('shopify:section:load', event => this.initialize(event.target));
    document.addEventListener('shopify:section:unload', event => {
      if (this.modal && event.target.contains(this.modal.element)) this.releaseModal(this.modal.element);
    });
  }

  initialize(scope) {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.bindVariantPickers(scope);
    this.bindGalleries(scope);
    this.bindNewsletter(scope);
    this.bindEnter(scope);
    if (new URLSearchParams(location.search).get('topic') === 'return') {
      scope.querySelectorAll('[data-contact-topic]').forEach(select => { select.value = 'Return or exchange'; });
    }
    scope.querySelectorAll('video[autoplay]').forEach(video => {
      if (this.reducedMotion.matches || navigator.connection?.saveData) { video.pause(); video.autoplay = false; }
    });
  }

  storage(type, key, value) {
    try {
      const storage = window[type];
      if (value === undefined) return storage.getItem(key);
      storage.setItem(key, String(value));
    } catch { return null; }
  }

  activateModal(element, close, first, opener) {
    if (this.modal?.element === element) return;
    if (this.modal) this.modal.close();
    const previous = opener || document.activeElement;
    const siblings = new Map();
    element.inert = false;
    let branch = element;
    while (branch.parentElement && branch !== document.body) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || sibling === this.shade || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) continue;
        siblings.set(sibling, sibling.inert);
        sibling.inert = true;
      }
      branch = branch.parentElement;
    }
    this.modal = { element, close, previous, siblings };
    this.body.classList.add('modal-open');
    const target = first || this.focusable(element)[0] || element;
    if (target === element) element.tabIndex = -1;
    target.focus({ preventScroll: true });
  }

  releaseModal(element) {
    if (this.modal?.element !== element) return;
    const modal = this.modal;
    this.modal = null;
    modal.siblings.forEach((value, node) => { node.inert = value; });
    this.body.classList.remove('modal-open');
    if (modal.previous?.isConnected && !modal.previous.closest('[inert]')) modal.previous.focus({ preventScroll: true });
  }

  focusable(element) {
    return [...element.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]:not([tabindex="-1"])')]
      .filter(node => !node.disabled && node.tabIndex >= 0 && !node.closest('[hidden],[inert]') && node.getClientRects().length);
  }

  bindEvents() {
    document.addEventListener('keydown', event => {
      if (!this.modal) return;
      if (event.key === 'Escape') { event.preventDefault(); this.modal.close(); return; }
      if (event.key !== 'Tab') return;
      const nodes = this.focusable(this.modal.element);
      const first = nodes[0];
      const last = nodes.at(-1);
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && (document.activeElement === first || !nodes.includes(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !nodes.includes(document.activeElement))) { event.preventDefault(); first.focus(); }
    });
    document.addEventListener('click', event => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-cart-open]') && this.drawer) { event.preventDefault(); this.openCart(); }
      if (target.closest('[data-cart-close]') || target === this.shade) this.closeCart();
      if (target.closest('[data-menu-open]')) this.openMenu();
      if (target.closest('[data-menu-close]')) this.closeMenu();
      if (target.closest('[data-mobile-menu] a') && !target.closest('[data-cart-open]')) this.closeMenu();
      const control = target.closest('[data-quantity-change]');
      if (control) {
        const input = control.closest('[data-quantity]')?.querySelector('input');
        if (input && !input.disabled) {
          const delta = Number(control.dataset.quantityChange);
          if (delta > 0) input.stepUp(); else input.stepDown();
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      const remove = target.closest('[data-cart-remove]');
      if (remove) { event.preventDefault(); this.changeCart(remove.dataset.cartRemove, 0); }
      const motion = target.closest('[data-motion-toggle]');
      if (motion) {
        const section = motion.closest('.continuous-showreel, .marquee-banner');
        const paused = section.classList.toggle('is-paused');
        motion.setAttribute('aria-pressed', String(paused));
        motion.textContent = paused ? motion.dataset.playLabel : motion.dataset.pauseLabel;
      }
      if (target.closest('[data-password-open]')) {
        const modal = document.querySelector('[data-password-modal]');
        if (modal) {
          modal.hidden = false;
          this.activateModal(modal, () => this.closePassword(), modal.querySelector('input[type="password"]'));
        }
      }
      if (target.closest('[data-password-close]') || target.matches('[data-password-modal]')) this.closePassword();
    });
    document.addEventListener('change', event => {
      const input = event.target;
      if (input.matches('[data-cart-quantity]') && input.checkValidity()) this.changeCart(input.dataset.cartKey, Number(input.value));
      if (input.matches('[data-language-select]')) input.form?.requestSubmit();
    });
    document.addEventListener('submit', event => {
      if (event.target.matches('[data-product-form]')) { event.preventDefault(); this.addToCart(event.target); }
      if (event.target.matches('[data-newsletter-form]')) this.storage('sessionStorage', 'ilNewsletterSource', event.target.dataset.newsletterForm);
    });
  }

  openCart(opener) {
    if (!this.drawer) return;
    this.closeMenu();
    clearTimeout(this.shadeTimer);
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    if (this.shade) { this.shade.hidden = false; this.shade.classList.add('is-visible'); }
    this.activateModal(this.drawer, () => this.closeCart(), this.drawer.querySelector('[data-cart-close]'), opener);
  }

  closeCart() {
    if (!this.drawer) return;
    this.releaseModal(this.drawer);
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.inert = true;
    this.shade?.classList.remove('is-visible');
    this.shadeTimer = setTimeout(() => { if (this.shade && !this.drawer?.classList.contains('is-open')) this.shade.hidden = true; }, 260);
  }

  openMenu() {
    const menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;
    this.body.classList.add('menu-open');
    menu.setAttribute('aria-hidden', 'false');
    document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'true');
    this.activateModal(menu, () => this.closeMenu(), menu.querySelector('[data-menu-close]'));
  }

  closeMenu() {
    const menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;
    this.releaseModal(menu);
    this.body.classList.remove('menu-open');
    menu.setAttribute('aria-hidden', 'true');
    menu.inert = true;
    document.querySelector('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
  }

  closePassword() {
    const modal = document.querySelector('[data-password-modal]');
    if (!modal) return;
    this.releaseModal(modal);
    modal.hidden = true;
  }

  async addToCart(form) {
    if (form.dataset.submitting === 'true' || !form.reportValidity()) return;
    const root = form.closest('[data-product-root]');
    const button = form.querySelector('[data-product-submit]');
    const error = form.querySelector('[data-product-error]');
    const status = form.querySelector('[data-product-status]');
    const id = form.querySelector('[name="id"]');
    if (!id?.value || id.disabled || button?.disabled) { if (error) error.textContent = this.strings.chooseVariant; return; }
    const data = new FormData(form);
    data.set('sections', 'cart-drawer');
    data.set('sections_url', this.root + 'cart');
    form.dataset.submitting = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (error) error.textContent = '';
    if (status) status.textContent = '';
    try {
      const response = await fetch(this.root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || this.strings.addError);
      if (status) status.textContent = this.strings.added;
      try {
        await this.refreshCart(payload.sections?.['cart-drawer']);
        this.openCart(button);
      } catch {
        if (status) {
          status.textContent = this.strings.refreshError + ' ';
          const link = document.createElement('a');
          link.href = this.root + 'cart';
          link.textContent = document.querySelector('[data-cart-open]')?.textContent.trim() || this.strings.added;
          link.className = 'text-link';
          status.append(link);
        }
      }
    } catch (caught) {
      if (error) error.textContent = caught.message === 'Failed to fetch' ? this.strings.addError : caught.message || this.strings.addError;
    } finally {
      form.dataset.submitting = 'false';
      button.disabled = root?.dataset.available === 'false';
      button.removeAttribute('aria-busy');
    }
  }

  async refreshCart(sectionHTML) {
    const requests = [fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })];
    if (!sectionHTML) requests.push(fetch(this.root + 'cart?section_id=cart-drawer', { cache: 'no-store' }));
    const responses = await Promise.all(requests);
    if (!responses[0].ok) throw new Error(this.strings.cartError);
    const cart = await responses[0].json();
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    if (!sectionHTML) {
      if (!responses[1].ok) throw new Error(this.strings.cartError);
      sectionHTML = await responses[1].text();
    }
    const fresh = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector('[data-cart-drawer]');
    if (!fresh || !this.drawer) throw new Error(this.strings.cartError);
    this.drawer.innerHTML = fresh.innerHTML;
    return cart;
  }

  async changeCart(key, quantity) {
    if (this.cartBusy || !Number.isInteger(quantity) || quantity < 0) return;
    this.cartBusy = true;
    const controls = [...this.drawer.querySelectorAll('input,button')];
    const previousDisabled = controls.map(node => node.disabled);
    controls.forEach(node => { if (!node.matches('[data-cart-close]')) node.disabled = true; });
    this.drawer.setAttribute('aria-busy', 'true');
    const error = this.drawer.querySelector('[data-cart-error]');
    if (error) error.textContent = '';
    try {
      const response = await fetch(this.root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'], sections_url: this.root + 'cart' }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || this.strings.cartError);
      await this.refreshCart(payload.sections?.['cart-drawer']);
      const live = document.querySelector('[data-cart-live]');
      if (live) live.textContent = this.strings.cartUpdated;
      if (this.modal?.element === this.drawer) {
        const next = [...this.drawer.querySelectorAll('[data-cart-key]')].find(input => input.dataset.cartKey === key);
        (next || this.drawer.querySelector('[data-cart-close]'))?.focus({ preventScroll: true });
      }
    } catch (caught) {
      this.drawer.querySelectorAll('[data-cart-quantity]').forEach(input => { if (input.dataset.originalQuantity) input.value = input.dataset.originalQuantity; });
      const message = this.drawer.querySelector('[data-cart-error]');
      if (message) message.textContent = caught.message === 'Failed to fetch' ? this.strings.cartError : caught.message || this.strings.cartError;
    } finally {
      controls.forEach((node, index) => { if (node.isConnected) node.disabled = previousDisabled[index]; });
      this.drawer.removeAttribute('aria-busy');
      this.cartBusy = false;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: document.documentElement.dataset.currency || window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
  }

  bindVariantPickers(scope) {
    scope.querySelectorAll('[data-product-root]').forEach(root => {
      if (root.dataset.bound) return;
      root.dataset.bound = 'true';
      const form = root.querySelector('[data-product-form]');
      const data = root.querySelector('[data-variants-json]');
      if (!form || !data) return;
      let variants;
      try { variants = JSON.parse(data.textContent); } catch { return; }
      const idInput = form.querySelector('[name="id"]');
      const button = form.querySelector('[data-product-submit]');
      if (!idInput || !button) return;
      const inputs = [...root.querySelectorAll('[data-option-input]')];
      const selectedOptions = () => inputs.filter(input => input.type === 'hidden' || input.checked).sort((a,b) => Number(a.dataset.optionPosition)-Number(b.dataset.optionPosition)).map(input => input.value);
      const colorIndex = Number(root.dataset.colorOptionPosition || 0) - 1;
      let chosen = new URLSearchParams(location.search).has('variant') || inputs.every(input => input.type === 'hidden');
      let current;
      const sticky = root.querySelector('[data-mobile-purchase]');
      const stickyButton = root.querySelector('[data-sticky-purchase]');
      const chooseLabel = stickyButton?.textContent;
      const mediaId = variant => variant?.featured_media?.id || variant?.featured_image?.id;
      const apply = (variant, updateURL = true) => {
        current = variant;
        root.dataset.available = String(Boolean(variant?.available));
        idInput.disabled = !variant;
        idInput.value = variant ? String(variant.id) : '';
        button.disabled = !variant?.available || form.dataset.submitting === 'true';
        button.textContent = !variant ? this.strings.unavailable : variant.available ? button.dataset.addLabel + ' — ' + this.formatMoney(variant.price) : button.dataset.soldLabel;
        const selected = selectedOptions();
        inputs.filter(input => input.type === 'radio').forEach(input => {
          const index = Number(input.dataset.optionPosition) - 1;
          const possible = variants.some(item => item.available && item.options[index] === input.value && item.options.every((value, prior) => prior >= index || value === selected[prior]));
          input.disabled = !possible;
          input.closest('.variant-pill')?.classList.toggle('is-unavailable', !possible);
        });
        if (variant) {
          root.querySelectorAll('[data-product-price],[data-sticky-price]').forEach(price => { price.textContent = this.formatMoney(variant.price); });
          const compare = root.querySelector('[data-product-compare-price]');
          const sale = Boolean(variant.compare_at_price > variant.price);
          root.querySelector('[data-product-price]')?.classList.toggle('price--sale', sale);
          if (compare) { compare.hidden = !sale; compare.textContent = sale ? this.formatMoney(variant.compare_at_price) : ''; }
          const media = mediaId(variant) || (colorIndex >= 0 ? mediaId(variants.find(item => item.options[colorIndex] === selected[colorIndex] && mediaId(item))) : null);
          const slides = [...root.querySelectorAll('[data-media-id]')];
          const target = slides.find(slide => String(slide.dataset.mediaId) === String(media));
          const track = root.querySelector('[data-gallery-track]');
          if (target && track) {
            if (root.dataset.variantImageOnly === 'true') slides.forEach(slide => { slide.hidden = slide !== target; slide.classList.toggle('is-active', slide === target); });
            else track.scrollTo({ left: slides.indexOf(target) * track.clientWidth, behavior: updateURL && !this.reducedMotion.matches ? 'smooth' : 'auto' });
          }
        }
        if (stickyButton) {
          stickyButton.textContent = chosen ? button.textContent : chooseLabel;
          stickyButton.disabled = chosen && !variant?.available;
        }
        if (updateURL) {
          const url = new URL(location.href);
          if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
          history.replaceState({}, '', url.pathname + url.search + url.hash);
        }
      };
      inputs.forEach(input => input.addEventListener('change', () => {
        if (input.dataset.productUrl) { location.assign(input.dataset.productUrl); return; }
        chosen = true;
        const selected = selectedOptions();
        apply(variants.find(item => item.options.every((value,index) => value === selected[index])));
      }));
      inputs.filter(input => input.type === 'radio').forEach(input => input.addEventListener('click', () => {
        if (!chosen && current && current.options.every((value,index) => value === selectedOptions()[index])) { chosen = true; apply(current,false); }
      }));
      idInput.addEventListener('change', () => { chosen = true; apply(variants.find(item => String(item.id) === idInput.value)); });
      const initial = variants.find(item => String(item.id) === idInput.value);
      if (initial) apply(initial, false);
      if (inputs.length || variants.length === 1) root.classList.add('has-variant-ui');
      if (sticky) {
        const observer = new IntersectionObserver(entries => {
          sticky.hidden = entries[0].isIntersecting;
          this.body.classList.toggle('has-mobile-purchase', !entries[0].isIntersecting);
        });
        observer.observe(button);
        stickyButton.addEventListener('click', () => {
          if (chosen && current?.available) form.requestSubmit(button);
          else {
            form.scrollIntoView({ behavior: this.reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
            (inputs.find(input => input.type === 'radio' && !input.disabled) || idInput)?.focus({ preventScroll: true });
          }
        });
      }
    });
  }

  bindGalleries(scope) {
    scope.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (gallery.dataset.bound) return;
      gallery.dataset.bound = 'true';
      const track = gallery.querySelector('[data-gallery-track]');
      const slides = [...gallery.querySelectorAll('[data-media-id]')];
      const dots = [...gallery.querySelectorAll('[data-gallery-dot]')];
      if (!track || slides.length < 2) return;
      const index = () => Math.max(0, Math.min(slides.length-1, Math.round(track.scrollLeft / Math.max(track.clientWidth,1))));
      const go = next => track.scrollTo({ left: ((next + slides.length) % slides.length) * track.clientWidth, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
      const update = () => dots.forEach((dot,i) => { dot.classList.toggle('is-active', i === index()); dot.setAttribute('aria-current', String(i === index())); });
      track.addEventListener('scroll', update, { passive: true });
      dots.forEach((dot,i) => dot.addEventListener('click', () => go(i)));
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => go(index()-1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => go(index()+1));
      new ResizeObserver(update).observe(track);
      update();
    });
  }

  bindNewsletter(scope) {
    const root = scope.querySelector('[data-il-newsletter]');
    if (!root || root.dataset.bound) return;
    root.dataset.bound = 'true';
    const source = this.storage('sessionStorage','ilNewsletterSource');
    const successful = Boolean(document.querySelector('[data-newsletter-success]'));
    const errors = Boolean(root.querySelector('[data-newsletter-errors]'));
    const joined = this.storage('localStorage','ilNewsletterJoined') === '1';
    if (successful) this.storage('localStorage','ilNewsletterJoined','1');
    const dismissed = Number(this.storage('localStorage','ilNewsletterDismissedAt') || 0);
    const days = Number(root.dataset.dismissDays || 14);
    const close = () => { this.releaseModal(root); root.hidden = true; this.storage('localStorage','ilNewsletterDismissedAt',Date.now()); };
    const open = () => {
      root.hidden = false;
      const first = root.querySelector('[data-newsletter-errors], [data-newsletter-success]') || root.querySelector('input[type="email"]');
      this.activateModal(root, close, first);
    };
    root.querySelectorAll('[data-il-newsletter-close]').forEach(button => button.addEventListener('click',close));
    root.querySelector('[data-il-copy-code]')?.addEventListener('click',async event => {
      const copyButton = event.currentTarget;
      const status = root.querySelector('[data-copy-status]');
      const code = root.querySelector('[data-il-newsletter-code]');
      try { await navigator.clipboard.writeText(code.textContent.trim()); copyButton.textContent = this.strings.copied; if (status) status.textContent = this.strings.copied; }
      catch { if (status) status.textContent = this.strings.copyError; const range = document.createRange(); range.selectNodeContents(code); const selection = getSelection(); selection.removeAllRanges(); selection.addRange(range); }
    });
    if ((successful || errors) && (source === 'popup' || location.hash.startsWith('#NewsletterPopup-'))) { open(); return; }
    if (successful || joined || (dismissed && Date.now()-dismissed < days*86400000) || this.body.classList.contains('template-cart') || window.Shopify?.designMode) return;
    let engaged = false;
    let delayPassed = false;
    let shown = false;
    const blocked = () => this.modal || document.hidden || document.activeElement?.matches('input,select,textarea') || [...document.querySelectorAll('#shopify-pc__banner,#shopify-pc__prefs,[data-il-cookie]:not([hidden])')].some(node => node.getClientRects().length);
    const cleanup = () => { document.removeEventListener('pointerdown',interact); document.removeEventListener('keydown',interact); window.removeEventListener('scroll',interact); clearInterval(timer); };
    const attempt = () => { if (!root.isConnected) { cleanup(); return; } if (!shown && engaged && delayPassed && !blocked()) { shown = true; cleanup(); open(); } };
    const interact = () => { engaged = true; };
    document.addEventListener('pointerdown',interact,{ passive:true });
    document.addEventListener('keydown',interact);
    window.addEventListener('scroll',interact,{ passive:true });
    const timer = setInterval(attempt,1000);
    setTimeout(() => { delayPassed = true; attempt(); },Math.max(9000,Number(root.dataset.delay || 15000)));
  }

  bindEnter(scope) {
    const root = scope.querySelector('[data-enter-page]');
    if (!root || root.dataset.bound) return;
    root.dataset.bound = 'true';
    const once = root.dataset.enterOnce === 'true';
    if (!window.Shopify?.designMode && (this.reducedMotion.matches || (once && this.storage('sessionStorage','imprerialEnterPageSeenV1') === '1'))) { root.remove(); return; }
    root.hidden = false;
    const close = () => {
      if (once) this.storage('sessionStorage','imprerialEnterPageSeenV1','1');
      this.releaseModal(root); root.hidden = true;
      root.querySelectorAll('video').forEach(video => video.pause());
    };
    this.activateModal(root,close,root.querySelector('[data-enter-dismiss]'));
    root.querySelectorAll('[data-enter-dismiss]').forEach(button => button.addEventListener('click',close));
    root.querySelectorAll('[data-enter-link]').forEach(link => link.addEventListener('click',close));
    if (!window.Shopify?.designMode) setTimeout(close,Number(root.dataset.autoDismiss || 3)*1000);
  }
}

document.addEventListener('DOMContentLoaded', () => { window.editorialTheme = new EditorialTheme(); });
